import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { DotsThree, type Icon } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

// Per-row actions: hover-revealed fast-action icon buttons (0–3) followed by
// a 3-dot dropdown that contains the full action list. The component is
// purely presentational — wire `onSelect` per item.
//
// Layout convention: render this inside a sticky-right cell built with
// <StickyEndCell>. The parent row should carry the Tailwind `group` class so
// the `group-hover:opacity-100` reveal works.
//
// Mobile/touch: fast-action icons are hidden via `hidden md:flex` so users
// who can't hover never see icons that won't reveal. The 3-dot menu remains
// the universal entry point.

export interface RowAction {
  id: string;
  label: string;
  icon?: Icon;
  onSelect: () => void;
  disabled?: boolean;
  // Destructive actions render with the foreground "danger" treatment in
  // the dropdown and sit at the bottom of the menu, separated by a divider.
  destructive?: boolean;
}

interface Props {
  // Up to 3 quick actions surfaced as inline icons on row hover. Pass [] to
  // skip the inline strip entirely (the 3-dot menu still renders).
  fastActions?: RowAction[];
  // Full action list shown in the 3-dot dropdown. Should typically include
  // every fast action again — the inline icons are an affordance, not the
  // canonical surface (which is the menu).
  menuActions: RowAction[];
  // Aria label for the 3-dot trigger; defaults to a translated string.
  triggerLabel?: string;
  className?: string;
}

export function RowActions({
  fastActions = [],
  menuActions,
  triggerLabel,
  className,
}: Props) {
  const { t } = useTranslation('common');
  const trimmedFast = fastActions.slice(0, 3);

  // Split menu actions: non-destructive first, destructive last with a
  // divider in between. Destructive actions get a foreground accent so they
  // visually separate from routine items.
  const safe = menuActions.filter((a) => !a.destructive);
  const dangerous = menuActions.filter((a) => a.destructive);

  const hasFast = trimmedFast.length > 0;

  return (
    <div className={cn('relative flex h-full items-center justify-end', className)}>
      {/* Single combined pill containing the fast actions and the 3-dot
          trigger. At rest the pill is just the dots button (transparent
          border/bg). On row hover/focus the pill background appears and
          the fast-actions container animates from max-width 0 to its
          natural width, sliding in from the right of the dots button.
          The pill grows leftward, escaping the ~64px sticky cell and
          floating over the data column behind it. */}
      <div
        className={cn(
          'flex items-center rounded-pill border border-transparent transition-colors duration-200',
          // Pill chrome (border + card bg + shadow) is gated to md+ in
          // lockstep with the fast-action icons it frames. On touch there is
          // no hover-reveal, and mobile browsers keep the `:hover` state
          // latched after a tap — without the md: gate that latched hover
          // surfaces an empty floating pill around the lone 3-dot button,
          // reading as a stray detached box poking out of the row.
          hasFast && [
            'md:group-hover:border-border md:group-hover:bg-card md:group-hover:shadow-md',
            'md:group-focus-within:border-border md:group-focus-within:bg-card md:group-focus-within:shadow-md',
          ],
        )}
      >
        {/* Fast actions: hidden at rest (max-w-0, padding 0, opacity-0,
            slightly offset right). On hover/focus, max-width grows so the
            pill expands LEFTWARD into the data column (right edge of pill
            is anchored by `justify-end` on the outer container). Opacity
            fades in alongside, and the small `translate-x` offset means
            the icons appear to settle leftward into place — and on exit,
            they fade out + drift leftward instead of sliding rightward
            into the dots button. md+ only — touch users without hover
            get just the 3-dot menu.
            `motion-reduce:` overrides drop the transform for users with
            prefers-reduced-motion (the eased fade still runs). */}
        {hasFast && (
          <div
            className={cn(
              'hidden md:flex md:items-center md:gap-1 md:max-w-0 md:overflow-hidden md:pl-0 md:pr-0',
              'md:opacity-0 md:translate-x-1',
              'md:transition-[max-width,padding,opacity,transform] md:duration-200',
              'md:motion-reduce:translate-x-0',
              'md:group-hover:max-w-[200px] md:group-hover:pl-1.5 md:group-hover:pr-1 md:group-hover:opacity-100 md:group-hover:translate-x-0',
              'md:group-focus-within:max-w-[200px] md:group-focus-within:pl-1.5 md:group-focus-within:pr-1 md:group-focus-within:opacity-100 md:group-focus-within:translate-x-0',
            )}
          >
            {trimmedFast.map((action) => {
              const ActionIcon = action.icon ?? DotsThree;
              return (
                <button
                  key={action.id}
                  type="button"
                  onClick={action.onSelect}
                  disabled={action.disabled}
                  aria-label={action.label}
                  title={action.label}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-pill text-foreground/70 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
                >
                  <ActionIcon className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={triggerLabel ?? t('rowActions.openMenu')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill text-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <DotsThree className="h-5 w-5" weight="bold" />
          </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[240px]">
          {safe.map((action) => {
            const ItemIcon = action.icon;
            return (
              <DropdownMenuItem
                key={action.id}
                disabled={action.disabled ?? false}
                onSelect={() => {
                  if (!action.disabled) action.onSelect();
                }}
              >
                {ItemIcon && <ItemIcon className="h-4 w-4" />}
                {action.label}
              </DropdownMenuItem>
            );
          })}
          {safe.length > 0 && dangerous.length > 0 && <DropdownMenuSeparator />}
          {dangerous.map((action) => {
            const ItemIcon = action.icon;
            return (
              <DropdownMenuItem
                key={action.id}
                disabled={action.disabled ?? false}
                className="text-foreground"
                onSelect={() => {
                  if (!action.disabled) action.onSelect();
                }}
              >
                {ItemIcon && <ItemIcon className="h-4 w-4" />}
                {action.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </div>
  );
}

// useHorizontalScrollEdge tracks whether the given scrollable element has
// content extending past its right viewport edge. Returns `isElevated`:
//   - true  when the element has horizontal overflow AND the user has not
//           scrolled all the way to the right
//   - false when the table fits in its container OR the user is at the
//           right edge
//
// Used to toggle the sticky-right column's elevation shadow: shadow visible
// only when there's something to "float over".
export function useHorizontalScrollEdge(
  ref: RefObject<HTMLElement | null>,
): boolean {
  const [isElevated, setIsElevated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    function check() {
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const hasOverflow = scrollWidth > clientWidth + 1;
      const atRight = scrollLeft + clientWidth >= scrollWidth - 1;
      setIsElevated(hasOverflow && !atRight);
    }

    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
    };
  }, [ref]);

  return isElevated;
}

interface StickyEndCellProps {
  // Background color class. Cell paints over the scrolling content beneath
  // it, so it must be opaque. Defaults to the table-row background.
  bgClass?: string;
  // Width of the cell. Defaults to w-16 (4rem) to exactly match the
  // ACTIONS_COL grid track every caller reserves — a narrower cell leaves a
  // transparent strip inside the track, so the button doesn't sit flush at
  // the right edge when the table is scrolled all the way right, and a sliver
  // of scrolling data peeks alongside it when elevated. The pill grows
  // leftward beyond this on hover and floats over the data column behind it,
  // so this constraint only governs the at-rest column width, not the hover
  // state.
  widthClass?: string;
  // True when the table has horizontal overflow AND the user is not at the
  // right edge. Drives the elevation shadow.
  elevated: boolean;
  className?: string;
  children: ReactNode;
}

// StickyEndCell is the wrapper for any "actions column" cell that should
// stick to the right edge of a horizontally-scrolling table. The shadow
// fades when the user scrolls all the way right, so it stops looking like
// a floating layer once there's nothing left to obscure.
export function StickyEndCell({
  bgClass = 'bg-background',
  widthClass = 'w-16',
  elevated,
  className,
  children,
}: StickyEndCellProps) {
  return (
    <div
      className={cn(
        // `justify-end` anchors RowActions to the cell's right edge so when
        // the pill grows on hover (icons revealing), it expands LEFTWARD
        // into the data column behind it instead of overflowing rightward
        // off the table edge.
        'sticky right-0 z-10 flex shrink-0 self-stretch items-center justify-end px-2 transition-shadow duration-150',
        widthClass,
        bgClass,
        // Soft, full-height left-cast shadow that reads as a continuous
        // vertical band against the scrolling content underneath. Without
        // self-stretch the cell can collapse to its content height in some
        // grid contexts and the shadow looks "stubby". Wider blur (30px)
        // diffuses the edge so it fades smoothly into the row instead of
        // landing as a hard band.
        elevated && 'shadow-[-12px_0_30px_-8px_rgba(0,0,0,0.18)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

// useElementRef is a tiny convenience for callers that need a typed ref to
// pass into useHorizontalScrollEdge — saves the import dance.
export function useElementRef<T extends HTMLElement>() {
  return useRef<T | null>(null);
}
